/**
 * SFCC catalog SEO-URL rules — the source of truth for how Workers generates
 * and resolves category URL slugs, kept in lockstep with Business Manager.
 *
 * Derived verbatim from the BM export `SEO → URL Rules`
 * (`sites/<SITE>/url-rules.xml`, `<general-settings>` + `<character-replacements>`).
 * Verified identical across FSASTORE / HSASTORE / WDH at the 2026-04 export
 * (only the replacement ordering differed between sites, which is a no-op here
 * because the two substitutions don't overlap the removals or each other).
 *
 * When BM URL Rules change, update THIS file (re-export and diff) rather than
 * editing slug logic in code. `slugifyCategoryName` (app/lib/utils/url.ts)
 * consumes it.
 *
 * NOTE — not everything BM applies is expressible here. Beyond the declared
 * `<character-replacements>`, SFCC also folds diacritics and collapses repeated
 * separators (confirmed against catalog ids: `Avène`→`avene`,
 * `Protein Bars / Powders / Shakes`→`protein-bars-powders-shakes`). That
 * platform normalization is NOT in the export; `slugifyCategoryName` layers it
 * on via `slugifyProductName` (lowercase + NFD fold + collapse + trim), which
 * also implements `lowercase`/`blankSubstitute` below.
 */
export interface UrlRules {
  /** `<lower-case-urls>` — enforced by `slugifyProductName`. */
  lowercase: boolean;
  /** `<blank-substitute>` — the separator spaces become; enforced by `slugifyProductName`. */
  blankSubstitute: string;
  /**
   * `<character-replacements>` — ordered `[character, replacement]`; an empty
   * replacement removes the character. Applied before the platform
   * normalization. These are the rules that genuinely diverge from a naive
   * slugifier (e.g. `&`→`and`, `+`→`plus`, apostrophes removed not hyphenated).
   */
  characterReplacements: ReadonlyArray<readonly [string, string]>;
  /** `<category-rule>` attribute — categories slug from their display name. */
  categoryAttribute: "displayName";
  /** `<product-rule>` attribute — products slug from their name. */
  productAttribute: "name";
}

export const DEFAULT_URL_RULES: UrlRules = {
  lowercase: true,
  blankSubstitute: "-",
  characterReplacements: [
    ["/", ""],
    ["&", "and"],
    [")", ""],
    [":", ""],
    ["+", "plus"],
    ["®", ""],
    ["!", ""],
    ["™", ""],
    ['"', ""],
    [",", ""],
    [";", ""],
    ["'", ""],
    ["%", ""],
    ["$", ""],
    ["(", ""],
    ["#", ""],
  ],
  categoryAttribute: "displayName",
  productAttribute: "name",
};

/**
 * Per-site rules. All three sites are identical today, but BM stores URL Rules
 * per-site, so this map lets a single site diverge later without touching the
 * slug engine.
 */
export const URL_RULES_BY_SITE: Record<string, UrlRules> = {
  FSASTORE: DEFAULT_URL_RULES,
  HSASTORE: DEFAULT_URL_RULES,
  WDH: DEFAULT_URL_RULES,
};
